import { IBrand, ICarImageElement } from 'data/schema';
import * as React from 'react';
import { IMicro2AppFeatureFlags } from './index';

// Lazy load Micro2DApp component for code splitting
const Micro2DApp = React.lazy(() => import('./index').then((mod) => ({ default: mod.default })));

export type GalleryProps = {
  assets: ICarImageElement[];
  userType?: 'dealer' | 'customer';
  firstAsset?: ICarImageElement;
  brand?: IBrand;
  theme?: string;
  template?: string;
  features?: IMicro2AppFeatureFlags;
  customerAllowance?: boolean;
};

/**
 * Loading fallback component for Micro2DApp
 */
const Micro2DAppFallback: React.FC = () => (
  <div
    className="flex size-full items-center justify-center rounded bg-gray-900"
    role="status"
    aria-label="Loading 2D gallery"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="size-12 animate-spin rounded-full border-b-2 border-white" />
      <p className="text-sm text-white">Loading gallery...</p>
    </div>
  </div>
);

/**
 * Gallery component - displays a single car's images in gallery mode.
 * Uses the `assets` prop with ICarImageElement[] structure.
 * Lazy loads Micro2DApp component for improved code splitting and performance.
 */
const Gallery: React.FC<GalleryProps> = ({
  assets,
  userType,
  firstAsset,
  brand,
  theme,
  template,
  features,
  customerAllowance,
}) => {
  const galleryAssets = React.useMemo(() => {
    // Filter assets to include only those with mode 'demo' or undefined
    return assets.filter((asset) => !asset.mode || asset.mode === 'demo');
  }, [assets]);

  return (
    <React.Suspense fallback={<Micro2DAppFallback />}>
      <Micro2DApp
        mode="gallery"
        assets={galleryAssets}
        userType={userType}
        firstAsset={firstAsset}
        brand={brand}
        theme={theme}
        template={template}
        features={features}
        customerAllowance={customerAllowance}
      />
    </React.Suspense>
  );
};

export default Gallery;
